import { isPaymentRequiredError } from '../../types/api/errors';
import { dayjs, formatDisplayDate } from '../date';
import { byDate, byNumber, sortList, thenBy } from '../list-sort';
import { formatDurationClock } from '../measurement';

import type {
  UserGoals,
  UserGoalStatus,
  UserGoalType,
} from '../../types/domain/user-goal.types';
import type { SortCatalog } from '../list-sort';

/**
 * Goal List Model
 *
 * The Goals list's derivations, shared by the web page and the mobile screen
 * so both read a goal the same way: the pace reading, the figures and their
 * units, the deadline phrasing, and the client-side filtering, sorting, counts
 * and hero pick. The pace reading is the important one: progress on its own
 * cannot say whether a goal is healthy, because 56% is winning or losing
 * depending on how much of the goal's time has already gone.
 *
 * The index endpoint returns the user's whole set in one response (see
 * `goal.store`), so every filter here runs on the client.
 */

// =============================================================================
// TYPES
// =============================================================================

/**
 * Goal status filter options (UI groupings of UserGoalStatus)
 *
 * - active: status === 'Active' — in flight, target date still ahead
 * - ongoing: status === 'Ongoing' — past its target date but not closed out
 * - completed: status === 'Completed'
 * - canceled: status === 'Canceled'
 * - all: everything, including 'Archived'
 */
export type GoalStatusFilter =
  'active' | 'ongoing' | 'completed' | 'canceled' | 'all';

/**
 * How the list is ordered.
 *
 * - jeopardy: furthest behind pace first — the default, because the page
 *   exists to answer "what needs me?"
 * - deadline: soonest target date first
 * - progress: furthest along first
 */
export type GoalSortOrder = 'jeopardy' | 'deadline' | 'progress';

/**
 * Goal filter state for the toolbar
 */
export interface GoalFilterState {
  /** Status grouping */
  status: GoalStatusFilter;
  /** Goal type filter (optional) */
  type?: UserGoalType;
  /** Search query for title/description */
  search?: string;
}

/**
 * Counts for the status filter badges
 */
export interface GoalStatusCounts {
  active: number;
  ongoing: number;
  completed: number;
  canceled: number;
  total: number;
}

/**
 * How a goal is tracking against its own timeline.
 *
 * This is the page's central idea: progress alone cannot say whether a goal is
 * healthy, because a goal at 56% is winning or losing depending on how much of
 * its time has already gone.
 *
 * - ahead: progress is meaningfully past where the elapsed time implies
 * - on-track: progress is within tolerance of expected, or trails it while the
 *   logged rate still lands it on time
 * - behind: progress trails expected, or leads it while the logged rate lands
 *   it late; the target date has not passed
 * - risk: far enough behind that the deadline will beat it
 * - overdue: the target date has passed and the goal is not closed
 * - done: completed
 * - none: no measurement logged yet, or the goal was canceled
 */
export type GoalPaceState =
  'ahead' | 'on-track' | 'behind' | 'risk' | 'overdue' | 'done' | 'none';

/**
 * The derived pace reading for a single goal — everything the pace bar and its
 * legend need, computed once per goal rather than recomputed per component.
 */
export interface GoalPaceReading {
  /** Which visual treatment applies */
  state: GoalPaceState;
  /** Actual progress, clamped to 0-100 */
  progress: number;
  /** Where progress should be by today, clamped to 0-100 */
  expected: number;
  /** Signed difference (progress - expected), rounded */
  delta: number;
  /** Human-readable verdict, e.g. "12% ahead of pace" */
  verdict: string;
}

/**
 * A measurement split into its numeral and its unit, so the two can be sized
 * independently — units are not uniformly short.
 */
export interface GoalMeasure {
  value: string;
  unit: string | null;
}

// =============================================================================
// PACE
// =============================================================================

/**
 * How far past expected counts as genuinely ahead rather than noise.
 */
const AHEAD_THRESHOLD = 5;

/**
 * How far behind expected counts as slipping rather than noise.
 */
const BEHIND_THRESHOLD = -5;

/**
 * How far behind expected means the deadline will realistically beat the goal.
 */
const RISK_THRESHOLD = -15;

function clampPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, value));
}

/**
 * Maps the status/pace combination onto a single visual state.
 */
function resolvePaceState(goal: UserGoals, delta: number): GoalPaceState {
  switch (goal.status) {
    case 'Completed':
      return 'done';
    case 'Canceled':
    case 'Archived':
      return 'none';
    case 'Ongoing':
      return 'overdue';
    default:
      break;
  }

  // An active goal with nothing logged has no pace to report — saying it is
  // "behind" would blame the user for a measurement they simply haven't taken.
  if (goal.current_value === null) return 'none';

  return resolveLineBand(delta);
}

/** The pace band the straight-line delta alone puts a goal in. */
function resolveLineBand(
  delta: number
): Extract<GoalPaceState, 'risk' | 'behind' | 'on-track' | 'ahead'> {
  if (delta <= RISK_THRESHOLD) return 'risk';
  if (delta < BEHIND_THRESHOLD) return 'behind';
  if (delta >= AHEAD_THRESHOLD) return 'ahead';
  return 'on-track';
}

/**
 * Where the API's `is_on_track` overrules the straight-line reading.
 *
 * The straight line says where progress sits today; `is_on_track` says whether
 * the logged rate lands it by the target date, and is the same velocity
 * projection behind `estimated_completion`. Read alone, the line put "18%
 * ahead of pace" above "you finish 52 days late" on one goal page. Without a
 * projection the API falls back to `progress >= expected`, so only an
 * optimistic line it calls off track, or a behind line it calls on track, can
 * be the projection speaking — a small shortfall stays inside the noise band.
 */
type PaceOverride = 'slowing' | 'recovering' | null;

function resolveOverride(
  goal: UserGoals,
  state: GoalPaceState,
  delta: number
): PaceOverride {
  if (
    !goal.is_on_track &&
    delta > 0 &&
    (state === 'ahead' || state === 'on-track')
  ) {
    return 'slowing';
  }
  if (goal.is_on_track && (state === 'behind' || state === 'risk')) {
    return 'recovering';
  }
  return null;
}

function resolveVerdict(
  state: GoalPaceState,
  delta: number,
  progress: number,
  override: PaceOverride
): string {
  if (override === 'slowing') return 'Ahead now, but slowing';
  if (override === 'recovering') return 'On course to finish on time';

  switch (state) {
    case 'ahead':
      return `${delta}% ahead of pace`;
    case 'behind':
    case 'risk':
      return `${Math.abs(delta)}% behind pace`;
    case 'overdue':
      return 'Past its target date';
    case 'done':
      return 'Target reached';
    case 'none':
      return progress > 0 ? `Stopped at ${progress}%` : 'Not started yet';
    case 'on-track':
    default:
      return 'On pace';
  }
}

/**
 * Derives everything the pace bar and its legend need for one goal.
 */
export function getPaceReading(goal: UserGoals): GoalPaceReading {
  // A completed goal reached its target by definition. Trusting a stored
  // percentage over that puts "0%" beside "Target reached" on the same card.
  const progress =
    goal.status === 'Completed'
      ? 100
      : Math.round(clampPercent(goal.progress_percentage));
  const expected = Math.round(clampPercent(goal.expected_progress_percentage));
  const delta = progress - expected;

  const lineState = resolvePaceState(goal, delta);
  const override = resolveOverride(goal, lineState, delta);
  const state =
    override === 'slowing'
      ? 'behind'
      : override === 'recovering'
        ? 'on-track'
        : lineState;

  return {
    state,
    progress,
    expected,
    delta,
    verdict: resolveVerdict(state, delta, progress, override),
  };
}

/**
 * Where a label centred on a pace-bar mark starts, in px from the bar's left
 * end, held inside the bar.
 *
 * The hero names its two marks inline — the `N%` chip over the fill's end and
 * `N% expected today` under the tick. Centred on its mark, a label near either
 * end runs past the bar, and the hero clips what overflows; so it slides just
 * far enough to sit flush with that end instead. Away from the ends it stays
 * centred. Null until both widths are measured, which callers draw as the
 * plain centred position.
 */
export function getPaceLabelLeft(
  percent: number,
  labelWidth: number,
  trackWidth: number
): number | null {
  if (trackWidth <= 0 || labelWidth <= 0) return null;
  const centred = (percent / 100) * trackWidth - labelWidth / 2;
  return Math.min(Math.max(0, centred), Math.max(0, trackWidth - labelWidth));
}

// =============================================================================
// FORMATTING
// =============================================================================

/**
 * Display abbreviations for units too long to sit beside a numeral.
 *
 * `sessions/week` is thirteen characters against `kg`'s two, which is enough
 * to wrap a scoreboard figure onto two lines and to collide adjacent labels on
 * the milestone rail. The full unit is kept in `title` attributes, so nothing
 * is lost — only shortened where space decides legibility.
 */
const SHORT_UNITS: Record<string, string> = {
  'sessions/week': '/wk',
  'sessions per week': '/wk',
};

/**
 * Abbreviates a unit for display, leaving already-short units untouched.
 */
export function shortenUnit(unit: string): string {
  return SHORT_UNITS[unit.toLowerCase()] ?? unit;
}

/**
 * Whether a unit already expresses a rate, e.g. `sessions/week`.
 *
 * A goal measured in a rate cannot report a meaningful "per week" rate of its
 * own — "0.13 sessions/week a week" is arithmetically correct and unreadable —
 * so the rate pair is suppressed for these rather than rendered as noise.
 */
export function isRateUnit(unit: string | null): boolean {
  return unit !== null && unit.includes('/');
}

/**
 * Units that measure elapsed time, and what one of them is worth in seconds.
 *
 * A run-time goal arrives as a raw count — `4025` — which nobody reads as an
 * hour and seven minutes. These get clock-formatted instead.
 */
const DURATION_UNITS: Record<string, number> = {
  second: 1,
  seconds: 1,
  secs: 1,
  sec: 1,
  minute: 60,
  minutes: 60,
  mins: 60,
  min: 60,
};

/**
 * Whether a unit is set apart from its numeral by a space.
 *
 * Percent is not: `20.5%` is how it is written everywhere, and `20.5 %` reads
 * as two separate things. Word units — `kg`, `min`, `sessions/week` — take the
 * space they would take in a sentence.
 */
export function unitNeedsSpace(unit: string | null): boolean {
  return unit !== null && unit !== '%';
}

/** Joins a formatted value to its unit with the spacing that unit wants. */
export function withGoalUnit(formatted: string, unit: string | null): string {
  if (!unit) return formatted;
  return unitNeedsSpace(unit) ? `${formatted} ${unit}` : `${formatted}${unit}`;
}

/** Whether a unit measures elapsed time and should be shown on a clock. */
export function isDurationUnit(unit: string | null): boolean {
  return unit !== null && unit.toLowerCase() in DURATION_UNITS;
}

/**
 * Decimal places a goal figure is worth showing.
 *
 * Values are computed, not entered — an estimated 1RM arrives as
 * `62.53229546833893`, and binary floats produce `62.010000000000005` from
 * arithmetic that should have given `62.01`. Neither belongs on screen, and at
 * display size the long one overruns its column into the text beside it.
 */
const MAX_DECIMALS = 2;

/** Rounds for display and drops trailing zeros, so `3` stays `3`. */
function trimNumber(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return Number(value.toFixed(MAX_DECIMALS)).toString();
}

/**
 * Renders one of a goal's values for display.
 *
 * Durations become `1:07:05` rather than `4025`, using the same clock format
 * the workout and exercise pages already use, so a time reads the same
 * wherever it appears. Everything else is rounded to a figure a person would
 * write down.
 */
export function formatGoalValue(value: number, unit: string | null): string {
  if (!isDurationUnit(unit)) return trimNumber(value);
  return formatDurationClock(
    value * DURATION_UNITS[(unit as string).toLowerCase()]
  );
}

/**
 * Splits a measurement into its numeral and its unit.
 *
 * The two are sized independently because units are not uniformly short —
 * `sessions/week` is thirteen characters against `kg`'s two, and rendering it
 * at the numeral's display size overflows the card.
 *
 * A clock-formatted duration returns no unit: `1:07:05` already says it is a
 * time, and "1:07:05 seconds" says it twice and wrongly.
 */
export function getGoalMeasure(
  value: number | null,
  unit: string | null
): GoalMeasure {
  if (value === null) return { value: '—', unit: null };
  if (isDurationUnit(unit)) {
    return { value: formatGoalValue(value, unit), unit: null };
  }
  return { value: formatGoalValue(value, unit), unit };
}

/**
 * Parses one of a goal's dates as a calendar day.
 *
 * Goal dates are calendar dates, but the API serialises them at midnight UTC.
 * Parsing them as instants shifts them back a day for any viewer west of UTC —
 * a 20 Jun target renders as "19 Jun" in Vancouver — so read the date part
 * only and let it be local.
 */
function toCalendarDay(value: string): ReturnType<typeof dayjs> {
  return dayjs(value.slice(0, 10), 'YYYY-MM-DD');
}

/**
 * How much of the metric is left to cover, null when nothing is logged.
 */
export function getRemainingToTarget(goal: UserGoals): number | null {
  if (goal.current_value === null) return null;
  return Math.abs(
    Math.round((goal.target_value - goal.current_value) * 100) / 100
  );
}

/**
 * The descriptor line under a goal's title, e.g.
 * `Strength Gain · Barbell Back Squat 1RM`.
 *
 * Prefers the exercise name over the generic metric label, because
 * "Exercise 1RM" is identical across every strength goal a user has.
 */
export function getGoalDescriptorParts(goal: UserGoals): string[] {
  // "Barbell Back Squat 1RM" reads as one phrase; "Squat and Overhead Punch
  // Run / Cycle Time" reads as one very confusing name, so anything longer
  // than the 1RM shorthand gets a separator.
  const detail = !goal.exercise?.name
    ? goal.target_metric
    : goal.target_metric === 'Exercise 1RM'
      ? `${goal.exercise.name} 1RM`
      : `${goal.exercise.name} · ${goal.target_metric}`;

  return [goal.type, detail];
}

/**
 * Days between today and the goal's target date. Negative once it has passed.
 */
export function getDaysToTarget(goal: UserGoals): number {
  return toCalendarDay(goal.target_date)
    .startOf('day')
    .diff(dayjs().startOf('day'), 'day');
}

/**
 * The deadline phrasing for a card footer, tuned to the goal's status.
 */
export function getDeadlineLabel(goal: UserGoals): string {
  const days = getDaysToTarget(goal);

  if (goal.status === 'Completed') return 'Achieved';
  if (goal.status === 'Canceled') return 'Canceled';
  if (days < 0) {
    const overdue = Math.abs(days);
    return `${overdue} ${overdue === 1 ? 'day' : 'days'} past target`;
  }
  if (days === 0) return 'Due today';
  return `${days} ${days === 1 ? 'day' : 'days'} left`;
}

/**
 * Formats a date for display, null-safe for optional projections.
 */
export function formatGoalDate(date: string | null): string | null {
  return formatDisplayDate(date, 'dateShort', { calendarDate: true });
}

// =============================================================================
// STATUS TRANSITIONS
// =============================================================================

/** A status change made from a goal's menu. */
export type GoalTransition = 'cancel' | 'resume';

/**
 * What to tell the user when a cancel or resume fails, worded like the
 * delete-with-undo toasts: the goal named in quotes, then the way forward.
 *
 * Null for a 402. The app-level paywall presenter answers that one, and a red
 * toast beneath its prompt would present the refusal twice.
 */
export function getGoalTransitionError(
  transition: GoalTransition,
  goal: Pick<UserGoals, 'title'>,
  error: unknown
): string | null {
  if (isPaymentRequiredError(error)) return null;
  return `Couldn't ${transition} "${goal.title}". Please try again.`;
}

// =============================================================================
// FILTERING, SORTING AND COUNTS
// =============================================================================

const STATUS_BY_FILTER: Record<
  Exclude<GoalStatusFilter, 'all'>,
  UserGoalStatus
> = {
  active: 'Active',
  ongoing: 'Ongoing',
  completed: 'Completed',
  canceled: 'Canceled',
};

/**
 * Maps a goal's status onto its toolbar grouping.
 */
export function getStatusGroup(status: UserGoalStatus): GoalStatusFilter {
  switch (status) {
    case 'Active':
      return 'active';
    case 'Ongoing':
      return 'ongoing';
    case 'Completed':
      return 'completed';
    case 'Canceled':
      return 'canceled';
    default:
      return 'all';
  }
}

/**
 * Applies the toolbar state to the goal list.
 *
 * Everything is client-side: the index endpoint returns the user's complete
 * set in one response, so there is nothing to round-trip for.
 */
export function filterGoals(
  goals: UserGoals[],
  filters: GoalFilterState,
  sort: GoalSortOrder = DEFAULT_GOAL_SORT
): UserGoals[] {
  let filtered = [...goals];

  if (filters.status !== 'all') {
    const status = STATUS_BY_FILTER[filters.status];
    filtered = filtered.filter((goal) => goal.status === status);
  }

  if (filters.type) {
    filtered = filtered.filter((goal) => goal.type === filters.type);
  }

  if (filters.search) {
    const term = filters.search.toLowerCase();
    filtered = filtered.filter(
      (goal) =>
        goal.title.toLowerCase().includes(term) ||
        (goal.description?.toLowerCase().includes(term) ?? false) ||
        (goal.exercise?.name.toLowerCase().includes(term) ?? false)
    );
  }

  return sortGoals(filtered, sort);
}

/** Pace bands for "Furthest behind", worst first. */
const JEOPARDY_RANK = {
  risk: 0,
  behind: 1,
  'on-track': 2,
  ahead: 3,
} as const;

/**
 * Where a goal falls in "Furthest behind": the band its card is drawn in.
 *
 * Ranking on delta alone put a card flagged "Ahead now, but slowing" below one
 * reading "On pace", once the card started following `is_on_track`. An overdue
 * goal has already missed its date, so it ranks with risk however close it
 * came; otherwise one 3% short sat in the on-track band. Done and not-started
 * goals take the band their delta implies.
 */
function getJeopardyRank(goal: UserGoals): number {
  const { state, delta } = getPaceReading(goal);
  if (state === 'overdue') return JEOPARDY_RANK.risk;
  return state in JEOPARDY_RANK
    ? JEOPARDY_RANK[state as keyof typeof JEOPARDY_RANK]
    : JEOPARDY_RANK[resolveLineBand(delta)];
}

/**
 * The Goals list's sorts. Jeopardy is the default because the page exists to
 * answer "what needs me?" — the goal furthest behind its own pace comes first:
 * by the band its card shows, then by delta within it.
 */
export const GOAL_SORTS: SortCatalog<UserGoals, GoalSortOrder> = {
  jeopardy: {
    label: 'Furthest behind',
    compare: thenBy(
      byNumber(getJeopardyRank, 'asc'),
      byNumber((goal) => getPaceReading(goal).delta, 'asc')
    ),
  },
  deadline: {
    label: 'Soonest deadline',
    compare: byDate((goal) => goal.target_date, 'asc'),
  },
  progress: {
    label: 'Most progress',
    compare: byNumber((goal) => goal.progress_percentage, 'desc'),
  },
};

export const DEFAULT_GOAL_SORT: GoalSortOrder = 'jeopardy';

/** Orders the filtered set. */
export function sortGoals(
  goals: UserGoals[],
  order: GoalSortOrder
): UserGoals[] {
  return sortList(goals, GOAL_SORTS, order);
}

/**
 * Counts for the status filter badges.
 */
export function getGoalStatusCounts(goals: UserGoals[]): GoalStatusCounts {
  const counts: GoalStatusCounts = {
    active: 0,
    ongoing: 0,
    completed: 0,
    canceled: 0,
    total: goals.length,
  };

  goals.forEach((goal) => {
    const group = getStatusGroup(goal.status);
    if (group !== 'all') {
      counts[group] += 1;
    }
  });

  return counts;
}

/**
 * The goal to feature in the hero: among Active goals, the soonest target date.
 *
 * Unambiguous because a goal is only Active while its target date is still in
 * the future — anything past due is Ongoing and cannot be picked here.
 */
export function findHeroGoal(goals: UserGoals[]): UserGoals | undefined {
  return goals
    .filter((goal) => goal.status === 'Active')
    .sort(
      (a, b) => dayjs(a.target_date).valueOf() - dayjs(b.target_date).valueOf()
    )[0];
}
